import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { normalize, schema } from 'normalizr';
import { combineReducers } from 'redux';
import {
    CreatePricelistDto,
    ErrorResponse,
    PricelistDto,
    PricelistPeriodDto,
    UpdatePricelistDto
} from '../../../common/PricePrgClient';
import { createAsyncAction } from '../../../utils';

const namespaces = {
    pricelists: 'pricelists',
    selectedPricelistIds: 'selectedPricelistIds',
    pricelistPeriods: 'pricelistPeriods',
    createPricelistDialog: 'createPricelistDialog',
    pricelistCustomers: 'pricelistCustomers'
};

const pricelistsEntity = new schema.Entity<PricelistDto>('pricelists', undefined, {
    idAttribute: 'pricelistId'
});

export const fetchPricelists = createAsyncAction<void, PricelistDto[]>(
    namespaces.pricelists,
    'fetchPricelists'
);
export const updatePricelist = createAsyncAction<
    { pricelistId: number; dto: UpdatePricelistDto },
    PricelistDto
>(namespaces.pricelists, 'updatePricelist');
export const createPricelist = createAsyncAction<CreatePricelistDto, PricelistDto, ErrorResponse>(
    namespaces.pricelists,
    'createPricelist'
);
export const deletePricelist = createAsyncAction<number, number>(namespaces.pricelists, 'deletePricelist');

const pricelistsSlice = createSlice({
    name: namespaces.pricelists,
    initialState: {} as { [pricelistId: number]: PricelistDto },
    reducers: {},
    extraReducers: builder =>
        builder
            .addCase(fetchPricelists.success, (state, action) => {
                const normalized = normalize(action.payload, [pricelistsEntity]);
                return normalized.entities.pricelists ?? {};
            })
            .addCase(updatePricelist.success, (state, action) => {
                const { payload } = action;
                state[payload.pricelistId] = payload;
            })
            .addCase(createPricelist.success, (state, action) => {
                state[action.payload.pricelistId] = action.payload;
            })
            .addCase(deletePricelist.success, (state, action) => {
                delete state[action.payload];
            })
});

const selectedPricelistIdsSlice = createSlice({
    name: namespaces.selectedPricelistIds,
    initialState: [] as number[],
    reducers: {
        pricelistsSelected: (state, action: PayloadAction<number[]>) => action.payload
    },
    extraReducers: builder =>
        builder.addCase(deletePricelist.success, (state, action) =>
            state.filter(plId => action.payload !== plId)
        )
});
export const { pricelistsSelected } = selectedPricelistIdsSlice.actions;

const pricelistPeriodsEntity = new schema.Entity<PricelistPeriodDto>('pricelistPeriods', undefined, {
    idAttribute: 'pricelistPeriodId'
});

export const fetchPricelistPeriods = createAsyncAction<number[], PricelistPeriodDto[]>(
    namespaces.pricelistPeriods,
    'fetchPricelistPeriods'
);

const pricelistPeriodsSlice = createSlice({
    name: namespaces.pricelistPeriods,
    initialState: {} as { [pricelistId: number]: PricelistPeriodDto },
    reducers: {},
    extraReducers: builder =>
        builder.addCase(fetchPricelistPeriods.success, (state, action) => {
            const normalized = normalize(action.payload, [pricelistPeriodsEntity]);
            return normalized.entities.pricelistPeriods ?? {};
        })
});

type PricelistDialogState = {
    isOpen: boolean;
    isCreating: boolean;
};
const createPricelistDialogSlice = createSlice({
    name: namespaces.createPricelistDialog,
    initialState: {
        isOpen: false,
        isCreating: false
    } as PricelistDialogState,
    reducers: {
        openCreatePricelistDialog: state => {
            state.isOpen = true;
        },
        closeCreatePricelistDialog: state => {
            state.isOpen = false;
        }
    },
    extraReducers: builder =>
        builder
            .addCase(createPricelist.request, state => {
                state.isCreating = true;
            })
            .addCase(createPricelist.success, state => {
                state.isCreating = false;
            })
            .addCase(createPricelist.failure, state => {
                state.isCreating = false;
            })
});

export const { closeCreatePricelistDialog, openCreatePricelistDialog } = createPricelistDialogSlice.actions;

export const fetchCustomersForPricelist = createAsyncAction<number, number[]>(
    namespaces.pricelistCustomers,
    'fetchCustomersForPricelist'
);

type ConnectPricelistDialogParams = {
    customerId: number | null;
    pricelistId: number | null;
};

export type CustomerPricelistConnection = {
    customerId: number;
    pricelistId: number;
};

export const connectPricelistCustomers = createAsyncAction<
    CustomerPricelistConnection,
    CustomerPricelistConnection
>(namespaces.pricelistCustomers, 'createCustomerPricelistConnection');

const connectPricelistDialogSlice = createSlice({
    name: namespaces.pricelistCustomers,
    initialState: {
        isVisible: false,
        pricelistId: null as number | null,
        customerId: null as number | null
    },
    reducers: {
        showConnectPricelistDialog: (state, action: PayloadAction<ConnectPricelistDialogParams>) => {
            state.isVisible = true;
            state.customerId = action.payload.customerId;
            state.pricelistId = action.payload.pricelistId;
        },
        hideConnectPricelistDialog: state => {
            state.isVisible = false;
            state.customerId = null;
            state.pricelistId = null;
        }
    }
});

export const { showConnectPricelistDialog, hideConnectPricelistDialog } = connectPricelistDialogSlice.actions;

const pricelistCustomersSlice = createSlice({
    name: namespaces.pricelistCustomers,
    initialState: [] as number[],
    reducers: {},
    extraReducers: builder =>
        builder
            .addCase(fetchCustomersForPricelist.success, (state, action) => action.payload)
            .addCase(connectPricelistCustomers.success, (state, action) => [
                ...state,
                action.payload.customerId
            ])
});

export default combineReducers({
    pricelists: pricelistsSlice.reducer,
    pricelistPeriods: pricelistPeriodsSlice.reducer,
    selectedPricelistIds: selectedPricelistIdsSlice.reducer,
    ui: combineReducers({
        createPricelistDialog: createPricelistDialogSlice.reducer,
        connectPricelistDialog: connectPricelistDialogSlice.reducer
    }),
    pricelistCustomerIds: pricelistCustomersSlice.reducer
});
